"use client";

import { useAuth } from "@/lib/context/AuthProvider";
import { useLocale } from "@/lib/context/LocaleProvider";
import { useUI } from "@/lib/context/UIProvider";
import { Badge } from "@/components/ui/Badge";
import { Check, Folder } from "@/components/ui/icons";

/**
 * Upsell tile for guests / free users — sits beside the Default project
 * and opens the Pro paywall.
 */
export function ProjectsProCta() {
  const { t } = useLocale();
  const { isPro } = useAuth();
  const { openProModal } = useUI();

  if (isPro) return null;

  return (
    <div
      className="relative flex h-full flex-col overflow-hidden rounded-[20px] border border-accent/25 bg-accent-soft/80 p-[18px]"
      style={{
        backgroundImage:
          "linear-gradient(155deg, color-mix(in srgb, var(--accent) 14%, transparent) 0%, transparent 55%, rgba(217,182,92,0.12) 100%)",
      }}
    >
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-accent text-white shadow-[0_1px_2px_rgba(45,32,50,0.08)]">
          <Folder size={18} />
        </div>
        <Badge variant="pro" size="md" label={t.badgePro} />
      </div>

      <h2 className="text-[16.5px] font-semibold tracking-[-0.25px] text-ink [text-wrap:pretty]">
        {t.projectsProCtaTitle}
      </h2>
      <p className="mt-1.5 text-[13px] leading-[1.45] text-ink-3 [text-wrap:pretty]">
        {t.projectsProCtaDesc}
      </p>

      <ul className="mt-3.5 flex flex-col gap-2">
        {t.projectsProCtaBenefits.map((benefit) => (
          <li
            key={benefit}
            className="flex items-start gap-2 text-[12.5px] leading-[1.4] text-ink-2"
          >
            <span className="mt-0.5 flex h-[16px] w-[16px] flex-none items-center justify-center rounded-full bg-accent text-white">
              <Check size={10} />
            </span>
            <span className="[text-wrap:pretty]">{benefit}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-5">
        <button
          type="button"
          onClick={openProModal}
          className="inline-flex h-[42px] w-full items-center justify-center gap-[7px] rounded-full bg-accent px-5 text-[13.5px] font-semibold text-white transition hover:brightness-105"
        >
          <span>{t.proModalCta}</span>
          <Badge
            variant="pro"
            size="sm"
            label={t.badgePro}
            className="border-white/75 bg-[rgba(26,24,22,0.28)]"
          />
        </button>
      </div>
    </div>
  );
}
